/**
 * BORRADORES: el trabajo a medias que no se pierde al navegar ni al cerrar.
 *
 * POR QUE EXISTE
 * --------------
 * Cada pantalla del sistema se monta cuando se entra y se desmonta cuando se
 * sale —`App` elige con un `switch` y `Suspense` trae el trozo solo cuando hace
 * falta—. Eso es bueno para el peso del bundle y es DEVASTADOR para lo que se
 * estaba capturando: el ticket armado a medias desaparecia al tocar "Inventario"
 * para consultar una existencia, y la entrega del proveedor —que se captura
 * renglon por renglon mientras alguien lee la nota en voz alta— se perdia entera
 * si sonaba el telefono y se cerraba la hoja.
 *
 * Lo reporto el dueño con el ticket lleno en pantalla: se navega, se vuelve, y
 * dice "Ticket vacio". No hay error y no hay aviso: el trabajo simplemente ya no
 * esta. Es la forma cara de fallar, la misma del §13.
 *
 * QUE ES UN BORRADOR, Y QUE NO ES
 * -------------------------------
 * Un borrador es CAPTURA A MEDIAS que vive en el aparato. No es un documento del
 * negocio: no toca inventario, no toca dinero, y el servidor no se entera de que
 * existe. Un ticket a medias guardado en el servidor seria una fuente eterna de
 * inventario fantasma, y eso se sigue sin hacer.
 *
 * Lo que se guarda es EXACTAMENTE lo que alguien tecleo o escaneo. Nunca estado
 * de interfaz —que hoja estaba abierta, que renglon tenia el foco, si la camara
 * estaba encendida—: eso se vuelve a decidir al montar, y un borrador que
 * reabre camaras y hojas al restaurarse asusta en vez de ayudar.
 *
 * LAS CUATRO REGLAS, Y POR QUE CADA UNA
 * -------------------------------------
 * 1. **Un borrador es de UNA persona y de UNA sucursal.** La llave lleva el uuid
 *    del usuario y el id de la tienda. No es prolijidad: el renglon del ticket
 *    carga `costCents`, y el costo no puede viajar a quien no puede verlo (§8).
 *    Con la llave marcada, el cajero que entra despues en la misma all-in-one no
 *    tiene ni como nombrar el borrador de la dueña. Y un ticket de Norte no
 *    puede aparecer en Centro, que desde el 22 de agosto de 2026 son dos
 *    catalogos distintos (§7).
 *
 * 2. **Un borrador es del DIA en que se capturo.** Al leer se compara el dia de
 *    calendario; si cambio, se descarta. Un ticket de ayer reapareciendo en la
 *    caja a las siete de la mañana se cobraria como de hoy, y nadie tendria por
 *    que sospecharlo. Un plazo en horas no sirve: cerrar a las diez de la noche
 *    y abrir a las siete son nueve horas, y cualquier plazo comodo las cubre.
 *    El reloj del aparato basta para esto y no contradice el §13 —ahi la regla
 *    es que el reloj del navegador no decide la FECHA DE NEGOCIO de una venta, y
 *    aqui no se fecha nada: se decide si un borrador del propio aparato sigue
 *    vigente—.
 *
 * 3. **Se valida con Zod al leer, siempre.** Lo que hay en `localStorage` lo
 *    escribio una version anterior de este mismo programa, y el dia que un
 *    renglon del ticket estrene campo, lo guardado deja de encajar. Sin validar,
 *    eso es la caja arrancando con un objeto incompleto y reventando al cobrar.
 *    Validado, es un borrador que se descarta en silencio, que es justo lo que
 *    debe pasar. Es la unica forma admitida de estrechar un `unknown` (§9).
 *
 * 4. **Esto NUNCA puede lanzar.** Va en el camino de la caja. `localStorage`
 *    lanza con la cuota llena, en algunos modos privados, y ni siquiera existe
 *    en las pruebas de Node. Si el borrador falla se pierde el borrador, jamas
 *    la venta. Misma regla que `lib/error-log.ts`.
 *
 * DONDE SE BORRA
 * --------------
 * - Al terminar lo que se capturaba (se cobro el ticket, se registro la
 *   entrada): lo hace quien lo capturo, llamando a `discard()` o a
 *   `clearDraft(key)` DENTRO del `onSuccess` de su mutacion — imperativo, no
 *   colgado de un efecto que quiza no corra antes del desmontaje.
 * - Al salir de la sesion: `clearAllDrafts()` desde `session.tsx`. El costo no
 *   sobrevive a la sesion de quien podia verlo.
 * - Al entrar: `DraftScopeProvider` purga lo que sea de otro usuario o de otro
 *   dia. Sin esa barrida, un borrador con costo se quedaria en el disco de la
 *   all-in-one hasta que a alguien se le ocurriera abrir la misma pantalla.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import type { ZodType } from 'zod';

/** Todas las llaves empiezan asi. Es lo unico que mira la barrida. */
const PREFIX = 'abarrotes.draft.';

/**
 * Sube cuando cambie la forma del SOBRE, no la de los datos.
 *
 * La forma de los datos ya la vigila Zod borrador por borrador, que es mas fino:
 * cambiarle un campo al renglon del ticket no tiene por que tirar el alta de
 * producto que alguien dejo a medias.
 */
const VERSION = 1;

/**
 * Tope de lo que se escribe, por borrador.
 *
 * `localStorage` comparte una cuota de unos 5 MB con la bitacora de fallos y con
 * la sesion de Supabase; un pedido de trescientos renglones no puede ser lo que
 * deje al sistema sin donde guardar el token. Pasarse NO pierde nada de lo que
 * hay en pantalla: lo unico que se pierde es la red de seguridad, y se dice en
 * la consola en vez de fallar callado.
 */
const MAX_BYTES = 512 * 1024;

interface Envelope {
  v: number;
  /** Milisegundos del reloj del APARATO. Para saber de que dia es, no para auditar. */
  at: number;
  data: unknown;
}

/**
 * La llave, y su orden importa: `<prefijo><usuario>.<sucursal>.<nombre>`.
 *
 * El usuario va PRIMERO para que la barrida distinga "esto es de otra persona"
 * con una comparacion de prefijo y sin partir la cadena. El nombre va al final
 * porque lleva puntos (`pos.cart`) y ahi no le estorba a nadie.
 */
export function draftKey(scope: string, name: string): string {
  return `${PREFIX}${scope}.${name}`;
}

/** El ambito de un usuario en una sucursal. `null` mientras no haya sesion. */
export function draftScope(userId: string | null, storeId: number | null): string | null {
  return userId !== null && storeId !== null ? `${userId}.${storeId}` : null;
}

/** True si los dos instantes caen en el mismo dia de calendario del aparato. */
function sameDay(a: number, b: number): boolean {
  const x = new Date(a);
  const y = new Date(b);
  return (
    x.getFullYear() === y.getFullYear() &&
    x.getMonth() === y.getMonth() &&
    x.getDate() === y.getDate()
  );
}

/** Abre el sobre. Devuelve null si no es uno o si es de otra version. */
function parseEnvelope(raw: string): Envelope | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { v, at, data } = parsed as { v?: unknown; at?: unknown; data?: unknown };
    if (v !== VERSION) return null;
    if (typeof at !== 'number' || !Number.isFinite(at)) return null;
    return { v, at, data };
  } catch {
    return null;
  }
}

/**
 * Lee un borrador. Devuelve `null` por cualquier motivo: no hay, es de otro dia,
 * no encaja con el esquema, el almacenamiento no existe.
 *
 * Lo que no encaja NO se deja ahi: se borra. Un borrador ilegible que se queda
 * es una llave que se vuelve a leer y a descartar en cada montaje, para siempre.
 */
export function readDraft<T>(key: string, schema: ZodType<T>): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return null;

    const envelope = parseEnvelope(raw);
    if (envelope === null || !sameDay(envelope.at, Date.now())) {
      localStorage.removeItem(key);
      return null;
    }

    const result = schema.safeParse(envelope.data);
    if (!result.success) {
      localStorage.removeItem(key);
      return null;
    }
    return result.data;
  } catch {
    return null;
  }
}

/** Guarda un borrador. No lanza nunca. */
export function writeDraft<T>(key: string, value: T): void {
  try {
    const envelope: Envelope = { v: VERSION, at: Date.now(), data: value };
    const raw = JSON.stringify(envelope);
    if (raw.length > MAX_BYTES) {
      // Callarse aqui seria prometer una red de seguridad que no existe.
      console.warn(`[borrador] ${key} no cabe (${raw.length} bytes); no se guarda`);
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, raw);
  } catch {
    /* Sin borrador se sigue vendiendo. Sin caja, no. */
  }
}

/** Borra un borrador. No lanza nunca. */
export function clearDraft(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* vacio a proposito */
  }
}

/** Todas las llaves de borrador que hay ahora mismo en el aparato. */
function draftKeys(): string[] {
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (key !== null && key.startsWith(PREFIX)) keys.push(key);
  }
  return keys;
}

/**
 * Borra TODOS los borradores del aparato. Es lo que corre al cerrar sesion.
 *
 * Se van los de todos los usuarios y todas las sucursales a proposito: quien
 * sale de la sesion en la all-in-one del mostrador esta entregando la maquina.
 */
export function clearAllDrafts(): void {
  try {
    for (const key of draftKeys()) localStorage.removeItem(key);
  } catch {
    /* vacio a proposito */
  }
}

/**
 * Barrida de entrada: se queda solo con lo de ESTE usuario y de HOY.
 *
 * Lo de otro usuario se va aunque sea de hoy —ahi puede haber costo—; lo del
 * mismo usuario en OTRA sucursal se queda, porque cambiar de tienda y volver
 * tiene que devolver el ticket donde estaba.
 */
export function purgeDrafts(userId: string): void {
  try {
    const mine = `${PREFIX}${userId}.`;
    const now = Date.now();
    for (const key of draftKeys()) {
      if (!key.startsWith(mine)) {
        localStorage.removeItem(key);
        continue;
      }
      const raw = localStorage.getItem(key);
      if (raw === null) continue;
      const envelope = parseEnvelope(raw);
      if (envelope === null || !sameDay(envelope.at, now)) localStorage.removeItem(key);
    }
  } catch {
    /* vacio a proposito */
  }
}

/* -------------------------------------------------------------------------- */
/* El ambito, repartido por contexto                                          */
/* -------------------------------------------------------------------------- */

/**
 * El ambito viaja por contexto y NO se lee de `session.tsx`.
 *
 * Si este modulo importara la sesion y la sesion importara este —que lo hace,
 * para barrer al salir— quedaria un ciclo de modulos: compila sin quejarse y
 * revienta en el navegador con un `undefined` al evaluar. Es el mismo motivo por
 * el que `routes.ts` no importa nada. Aqui la flecha va en un solo sentido: la
 * sesion monta el proveedor, y este archivo no sabe que la sesion existe.
 */
const DraftScopeContext = createContext<string | null>(null);

export function DraftScopeProvider({
  userId,
  storeId,
  children,
}: {
  userId: string | null;
  storeId: number | null;
  children: ReactNode;
}): ReactNode {
  const scope = useMemo(() => draftScope(userId, storeId), [userId, storeId]);

  // Barrida al saber quien entro, y otra vez si entra alguien distinto en el
  // mismo aparato sin recargar la pagina.
  useEffect(() => {
    if (userId !== null) purgeDrafts(userId);
  }, [userId]);

  return <DraftScopeContext.Provider value={scope}>{children}</DraftScopeContext.Provider>;
}

/**
 * `null` mientras no haya sesion y sucursal. Con ambito nulo no se guarda ni se
 * lee nada: es preferible perder un borrador a escribir uno sin dueño.
 */
export function useDraftScope(): string | null {
  return useContext(DraftScopeContext);
}

/* -------------------------------------------------------------------------- */
/* Los dos ganchos                                                            */
/* -------------------------------------------------------------------------- */

/**
 * LA LLAVE DE UN BORRADOR, ANCLADA A LA SUCURSAL EN QUE SE MONTO.
 *
 * Para las hojas que capturan: la captura pertenece al cajon de donde salio, y
 * si alguien cambiara de sucursal con la hoja abierta, seguir al ambito le
 * copiaria a Norte lo que se tecleo en Centro. `useDraft` es el unico que
 * sigue al ambito, porque es dueño del estado y puede CAMBIARLO por el de la
 * otra tienda; una llave suelta no puede.
 */
export function useAnchoredDraftKey(name: string | null): string | null {
  const live = useDraftScope();
  const scope = useRef(live).current;
  return scope === null || name === null ? null : draftKey(scope, name);
}

/**
 * LO QUE HABIA GUARDADO AL MONTAR. La mitad de lectura, sola.
 *
 * Se lee UNA vez y por eso la llave se congela: volver a leer cuando cambia algo
 * mas seria pisar lo que la persona acaba de teclear con lo que habia en disco.
 *
 * Existe suelta porque una hoja con el estado repartido en quince `useState`
 * necesita lo recuperado ANTES de poder construir la foto que va a espejar, y
 * eso no cabe en una sola llamada: seria pedirse a si misma.
 */
export function useRestoredDraft<T>(name: string | null, schema: ZodType<T>): T | null {
  const scope = useDraftScope();
  const firstKey = useRef(scope === null || name === null ? null : draftKey(scope, name));
  const [restored] = useState<T | null>(() =>
    firstKey.current === null ? null : readDraft(firstKey.current, schema),
  );
  return restored;
}

export interface DraftInfo {
  /** True si lo que hay en pantalla se recupero de un borrador. */
  restored: boolean;
  /** Borra el borrador y devuelve el estado a su valor inicial. */
  discard: () => void;
}

/**
 * CUANDO EL BORRADOR ES EL ESTADO. Se usa igual que `useState`.
 *
 * Es la puerta normal: el ticket, la captura de una entrega, los filtros de una
 * lista.
 *
 * Escribe SIN retraso, en el efecto del propio cambio. Un `debounce` de 300 ms
 * se ve inofensivo y pierde justo el ultimo movimiento —el que se hizo un
 * instante antes de tocar otra pantalla—, que es el caso que este archivo existe
 * para arreglar.
 */
export function useDraft<T>(
  name: string | null,
  schema: ZodType<T>,
  initial: T,
): [T, Dispatch<SetStateAction<T>>, DraftInfo] {
  const scope = useDraftScope();
  const key = scope === null || name === null ? null : draftKey(scope, name);

  const restoredValue = useRestoredDraft(name, schema);

  /*
   * El valor inicial se congela para dos cosas: para poder volver a el al
   * descartar, y para poder compararlo. Un borrador identico a lo que la
   * pantalla enseña recien abierta no es un borrador: es una llave de basura.
   * Se borra en vez de escribirse, y asi vaciar el ticket deja el aparato
   * exactamente como estaba.
   */
  const initialRef = useRef(initial);
  const emptyJson = useRef(JSON.stringify(initial));

  const [value, setValue] = useState<T>(restoredValue ?? initial);

  /*
   * CAMBIAR DE SUCURSAL CAMBIA DE BORRADOR, y esto no es un adorno.
   *
   * Sin esto, armar un ticket en Centro y cambiar a Norte dejaba el ticket en
   * pantalla —eso ya pasaba antes de guardar nada— y ademas lo ESCRIBIA en el
   * cajon de Norte, que desde el 22 de agosto de 2026 es otro catalogo: un
   * ticket de productos que en esa tienda no existen, ahora ademas persistente.
   * Medido en el navegador, no deducido.
   *
   * Se ajusta durante el render y no en un efecto: asi no hay un cuadro
   * intermedio enseñando el ticket de la tienda anterior. Es el patron que React
   * documenta para estado que se deriva de algo de fuera.
   *
   * Sigue sin RELEERSE por cualquier otro motivo, que era el punto de congelar
   * la llave: mientras la llave sea la misma, manda lo que hay en pantalla.
   */
  const [loadedKey, setLoadedKey] = useState(key);
  if (key !== loadedKey) {
    setLoadedKey(key);
    if (key !== null) {
      const next = readDraft(key, schema);
      if (next !== null) setValue(next);
      // Sin borrador en la llave nueva se empieza limpio, salvo que se venga de
      // "todavia no habia sesion": ahi no habia donde guardar y lo que hay en
      // pantalla es lo unico que existe.
      else if (loadedKey !== null) setValue(initialRef.current);
    }
  }

  useEffect(() => {
    if (key === null) return;
    if (JSON.stringify(value) === emptyJson.current) clearDraft(key);
    else writeDraft(key, value);
  }, [key, value]);

  const discard = useCallback(() => {
    if (key !== null) clearDraft(key);
    setValue(initialRef.current);
  }, [key]);

  const info = useMemo<DraftInfo>(
    () => ({ restored: restoredValue !== null, discard }),
    [restoredValue, discard],
  );

  return [value, setValue, info];
}

/**
 * CUANDO EL ESTADO YA ESTA REPARTIDO. Espeja una foto de lo capturado.
 *
 * La puerta de servicio, para una hoja que ya tiene quince `useState` sueltos
 * —el alta de producto—. Juntarlos en un objeto para poder usar `useDraft` seria
 * tocar medio centenar de sitios en el formulario mas complicado del sistema, y
 * ese cambio no arregla nada que este roto: solo mueve estado de sitio. Aqui la
 * hoja conserva sus campos, siembra cada uno con lo que devolvio
 * `useRestoredDraft`, y manda una foto entera cada vez que algo cambia.
 *
 * Solo GUARDA. La lectura es la otra mitad y va por separado porque tiene que
 * ocurrir antes: los campos se siembran con ella.
 *
 * `active: false` es PAUSA, no borrado: la hoja esta cerrada o lo que enseña ya
 * no es captura (un acuse), y el disco no se toca en ningun sentido. Cerrar una
 * hoja a medias tiene que CONSERVAR el borrador — es el caso entero.
 *
 * BORRAR es siempre un acto explicito, en el sitio donde la captura quedo
 * guardada en la base: `clearDraft(key)` dentro del `onSuccess`. Un borrado que
 * dependiera de que React alcance a correr un efecto antes de desmontar la hoja
 * funcionaria a veces, y "a veces" en un borrado significa entregas fantasma
 * ofrecidas mañana.
 *
 * `empty` es la foto de la hoja recien abierta: mientras la captura sea igual a
 * ella no se escribe nada, por el mismo motivo que en `useDraft`.
 */
export function useDraftMirror<T>(
  name: string | null,
  schema: ZodType<T>,
  snapshot: T,
  options?: { active?: boolean; empty?: T },
): void {
  /*
   * EL ESPEJO SE QUEDA ANCLADO A LA SUCURSAL EN QUE SE ABRIO, al reves que
   * `useDraft`, y la asimetria es deliberada.
   *
   * `useDraft` es dueño del estado, asi que puede SEGUIR al ambito: cambiar de
   * tienda le cambia el ticket por el de esa tienda. El espejo no es dueño de
   * nada —los campos viven en la hoja— asi que seguir al ambito seria copiarle
   * a Norte una captura que se tecleo en Centro. Anclado, lo que se escribio
   * sigue yendo al cajon de donde salio.
   */
  const key = useAnchoredDraftKey(name);
  const active = options?.active ?? true;

  const emptyJson = useRef(options?.empty === undefined ? null : JSON.stringify(options.empty));

  /*
   * La foto llega recien construida en cada render, asi que compararla por
   * identidad no sirve: hay que compararla ya serializada. Es una vuelta de
   * `JSON.stringify` por render de una hoja de captura, no por cada tecla de la
   * caja, y a cambio no se escribe en disco cuando no cambio nada.
   */
  const lastJson = useRef<string | null>(null);

  useEffect(() => {
    if (key === null || !active) return;
    const json = JSON.stringify(snapshot);

    /*
     * LA PRIMERA FOTO ES LINEA BASE, NO ESCRITURA. El espejo persiste lo que
     * CAMBIA mientras la hoja esta viva; la foto inicial no necesita disco —si
     * vino de un borrador, el borrador ya la tiene; si vino en blanco, no hay
     * nada que guardar—. Y no es una finura: la hoja que restaura SIEMBRA sus
     * campos en un efecto, asi que la primera pasada del espejo ve el estado
     * en blanco de antes de la siembra. Sin esta linea, esa pasada comparaba
     * blanco contra `empty` y BORRABA el borrador que la siembra estaba
     * leyendo — con StrictMode doblando efectos, la segunda siembra ya no
     * encontraba nada. Se vio en el navegador, con la hoja real.
     */
    if (lastJson.current === null) {
      lastJson.current = json;
      return;
    }
    if (json === lastJson.current) return;
    lastJson.current = json;

    /*
     * El esquema se comprueba SOLO en desarrollo, y no es paranoia.
     *
     * La lectura valida y descarta lo que no encaja, que es lo correcto en la
     * tienda; pero si la foto que se guarda deja de encajar —alguien agrega un
     * campo al formulario y no al esquema— el borrador se escribe bien y no se
     * recupera NUNCA, sin un solo error a la vista. Eso se caza aqui, en la
     * maquina de quien lo escribio, y no en el mostrador.
     */
    if (import.meta.env.DEV) {
      const check = schema.safeParse(snapshot);
      if (!check.success) {
        console.warn(`[borrador] ${key} no encaja con su esquema: no se podria recuperar`, check.error);
      }
    }

    if (emptyJson.current !== null && json === emptyJson.current) clearDraft(key);
    else writeDraft(key, snapshot);
  }, [key, active, schema, snapshot]);
}
